/**
 * Formatting composable
 * Provides formatting utilities with automatic language/currency from session
 * Wraps Utility formatting functions with session context
 */

import { useSessionValues } from '@/composables/useSessionValues'
import {
  formatCurrency as formatCurrencyUtil,
  formatDate as formatDateUtil,
  formatDateRange as formatDateRangeUtil,
  formatNumber as formatNumberUtil,
  formatPercentage as formatPercentageUtil,
  type NumberFormatOptions,
  type CurrencyFormatOptions,
  type DateFormatOptions,
} from '@/utils/Utility'

/** Currency formatting options with currency override */
export interface FormatCurrencyOptions extends CurrencyFormatOptions {
  currency?: string
}

/** Return type of useFormatting composable */
export interface UseFormattingReturn {
  formatCurrency: (value: number | null | undefined, options?: FormatCurrencyOptions) => string
  formatDate: (date: Date | string | null | undefined, options?: DateFormatOptions) => string
  formatDateRange: (
    startDate: Date | string | null | undefined,
    endDate: Date | string | null | undefined,
    options?: DateFormatOptions
  ) => string
  formatNumber: (value: number | null | undefined, options?: NumberFormatOptions) => string
  formatPercentage: (value: number, minDigits?: number, maxDigits?: number) => string
}

/**
 * Formatting utilities with session context
 * Automatically uses language and currency from session
 * 
 * @returns Formatting functions
 * 
 * @example
 * const { formatCurrency, formatDate } = useFormatting()
 * const price = formatCurrency(1500) // Uses session language/currency
 * const date = formatDate(new Date())
 */
export function useFormatting(): UseFormattingReturn {
  const { language, currency } = useSessionValues()

  /**
   * Format currency value
   * @param value - Value to format
   * @param options - Formatting options (divideBy100, currency override, etc.)
   * @returns Formatted currency string
   */
  const formatCurrency = (
    value: number | null | undefined,
    options: FormatCurrencyOptions = {}
  ): string => {
    const currencyToUse = options.currency || currency.value
    return formatCurrencyUtil(value, language.value ?? 'en-US', currencyToUse ?? 'EUR', options)
  }

  /**
   * Format single date
   * @param date - Date to format
   * @param options - Intl.DateTimeFormat options
   * @returns Formatted date string
   */
  const formatDate = (
    date: Date | string | null | undefined,
    options: DateFormatOptions = {}
  ): string => {
    return formatDateUtil(date, language.value ?? 'en-US', options)
  }

  /**
   * Format date range
   * @param startDate - Start date
   * @param endDate - End date
   * @param options - Intl.DateTimeFormat options
   * @returns Formatted date range string
   */
  const formatDateRange = (
    startDate: Date | string | null | undefined,
    endDate: Date | string | null | undefined,
    options: DateFormatOptions = {}
  ): string => {
    return formatDateRangeUtil(startDate, endDate, language.value ?? 'en-US', options)
  }

  /**
   * Format number
   * @param value - Value to format
   * @param options - Intl.NumberFormat options
   * @returns Formatted number string
   */
  const formatNumber = (
    value: number | null | undefined,
    options: NumberFormatOptions = {}
  ): string => {
    return formatNumberUtil(value, language.value ?? 'en-US', options)
  }

  /**
   * Format percentage
   * @param value - Percentage value (e.g., 10 for 10%)
   * @param minDigits - Minimum fraction digits (default: 0)
   * @param maxDigits - Maximum fraction digits (default: 0)
   * @returns Formatted percentage string
   */
  const formatPercentage = (value: number, minDigits = 0, maxDigits = 0): string => {
    return formatPercentageUtil(language.value ?? 'en-US', value, minDigits, maxDigits)
  }

  return {
    formatCurrency,
    formatDate,
    formatDateRange,
    formatNumber,
    formatPercentage,
  }
}